import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useWeek } from "@/context/WeekContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, X } from "lucide-react";

export default function Unmatched() {
  const { week } = useWeek();
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [suggestFor, setSuggestFor] = useState(null);
  const [form, setForm] = useState({ up_train: "", down_train: "", name: "" });

  const load = async () => {
    const [u, p] = await Promise.all([
      api.get("/unmatched", { params: { week_label: week } }),
      api.get("/pending-pairs"),
    ]);
    setRows(u.data.rows || []);
    setPending(p.data || []);
  };

  useEffect(() => { if (week) load(); }, [week]);

  const openSuggest = (t) => {
    setSuggestFor(t);
    setForm({ up_train: t.train_no, down_train: "", name: "" });
  };

  const submit = async () => {
    if (!form.name) { toast.error("Enter pair name"); return; }
    await api.post("/pending-pairs", form);
    toast.success("Suggestion submitted for approval");
    setSuggestFor(null);
    load();
  };

  const approve = async (id) => {
    await api.post(`/pending-pairs/${id}/approve`);
    toast.success("Pair approved into master");
    load();
  };

  const reject = async (id) => {
    await api.delete(`/pending-pairs/${id}`);
    toast.success("Suggestion rejected");
    load();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Unmatched Trains</h1>
        <p className="text-sm text-slate-500 mt-1">Trains found in upload but not present in the pair master.</p>
      </div>

      <Card className="border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-100 border-b border-slate-200 text-xs uppercase font-semibold tracking-wider text-slate-600">
          Unmatched in week {week || "—"}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-[11px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-2">Train #</th>
              <th className="text-left px-4 py-2">Complaints</th>
              <th className="text-left px-4 py-2">Sample</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-sm text-slate-400">No unmatched trains</td></tr>}
            {rows.map((r) => (
              <tr key={r.train_no} data-testid={`unmatched-row-${r.train_no}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono font-semibold">{r.train_no}</td>
                <td className="px-4 py-2.5 font-mono font-bold text-red-600">{r.count}</td>
                <td className="px-4 py-2.5 text-xs text-slate-600">
                  {r.samples.map((s, i) => <span key={i} className="mr-2">{s.head} · {s.coach || "—"}</span>)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button data-testid={`suggest-pair-${r.train_no}`} size="sm" variant="outline" onClick={() => openSuggest(r)}>
                    Suggest as new pair
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-100 border-b border-slate-200 text-xs uppercase font-semibold tracking-wider text-slate-600">
          Pending Suggestions ({pending.length})
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-[11px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-2">Up</th>
              <th className="text-left px-4 py-2">Down</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {pending.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-sm text-slate-400">No pending suggestions</td></tr>}
            {pending.map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="px-4 py-2.5 font-mono">{p.up_train}</td>
                <td className="px-4 py-2.5 font-mono">{p.down_train || "—"}</td>
                <td className="px-4 py-2.5">{p.name}</td>
                <td className="px-4 py-2.5 text-right space-x-2">
                  <Button data-testid={`approve-pending-${p.id}`} size="sm" onClick={() => approve(p.id)} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button data-testid={`reject-pending-${p.id}`} size="sm" variant="outline" onClick={() => reject(p.id)}>
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!suggestFor} onOpenChange={(v) => !v && setSuggestFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Suggest new pair</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Up train</label>
              <Input value={form.up_train} onChange={(e) => setForm({ ...form, up_train: e.target.value })} className="font-mono" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Down train (optional)</label>
              <Input value={form.down_train} onChange={(e) => setForm({ ...form, down_train: e.target.value })} className="font-mono" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Pair name</label>
              <Input data-testid="suggest-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Konkan Kanya Express" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuggestFor(null)}>Cancel</Button>
            <Button data-testid="submit-suggestion-btn" className="bg-blue-600 hover:bg-blue-500 text-white" onClick={submit}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
